import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../api";
import { ActivityItem } from "../../components/Activity";
import { PagedList } from "../../components/PagedList";
import type { ActivityRow } from "../../components/Activity";
import { timeAgo } from "../../format";
import { Guard } from "../../shell/Guard";
import { Card, EmptyState, PageHead } from "../../shell/ui";

interface InboxItem {
  id: number;
  kind: string;
  title: string;
  body: string;
  url: string;
  read: boolean;
  createdAt: string;
}

const ICON: Record<string, string> = {
  price_alert: "◎",
  daily_summary: "▤",
  social: "☺",
  followed_bot: "◇",
  bot_drawdown: "▼",
  bot_profit: "▲",
  liquidation: "⚠",
  bot_crashed: "⚠",
};

/** Alerts and messages about your bots: everything you were sent, whether or not a device rang. */
function Alerts() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["alerts", "inbox"],
    queryFn: () => api<{ items: InboxItem[]; unread: number }>("/alerts/inbox?limit=100"),
    refetchInterval: 30_000,
  });
  const read = useMutation({
    mutationFn: () => api("/alerts/inbox/read", { method: "POST", body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["unread"] }),
  });
  // Opening the tab is reading it; the highlight stays until the next visit.
  useEffect(() => {
    if (q.data && q.data.unread > 0) read.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.unread]);
  return (
    <Card flush>
      {q.data?.items.length === 0 ? (
        <EmptyState icon="•" title="No alerts yet">
          Trades and liquidations of your bots, price alerts you set, a daily summary and activity on your
          things land here. Choose what you hear about, and set price alerts, in{" "}
          <Link to="/settings">Settings</Link>.
        </EmptyState>
      ) : (
        <PagedList
          className="activity-list"
          items={q.data?.items ?? []}
          render={(a) => (
            <li key={a.id} className={`activity${a.read ? "" : " is-unread"}`}>
              <span aria-hidden="true" style={{ width: 28, textAlign: "center" }}>
                {ICON[a.kind] ?? "•"}
              </span>
              <div className="activity__body">
                <p>
                  <Link to={a.url}>{a.title}</Link>
                </p>
                {a.body && <p className="muted small">{a.body}</p>}
                <p className="muted small">{timeAgo(a.createdAt)}</p>
              </div>
            </li>
          )}
        />
      )}
    </Card>
  );
}

function Activity() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api<{ items: ActivityRow[] }>("/hub/notifications?limit=100"),
  });
  const read = useMutation({
    mutationFn: () => api("/hub/notifications/read", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["unread"] }),
  });
  useEffect(() => {
    if (q.data?.items.some((n) => n.read_at === null)) read.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);
  return (
    <Card flush>
      {q.data?.items.length === 0 ? (
        <EmptyState icon="•" title="All quiet">
          Comments, ratings, forks and replies on your things show up here, and trades by bots you follow.
        </EmptyState>
      ) : (
        <PagedList
          className="activity-list"
          items={q.data?.items ?? []}
          render={(a) => <ActivityItem key={`${a.id}`} a={a} />}
        />
      )}
    </Card>
  );
}

function Inner() {
  const [tab, setTab] = useState<"alerts" | "activity">("alerts");
  return (
    <div className="stack narrow">
      <PageHead title="Notifications">
        <Link className="btn btn--sm btn--ghost" to="/settings">
          Settings
        </Link>
      </PageHead>
      <nav className="subnav" aria-label="Notifications">
        {(
          [
            ["alerts", "Alerts"],
            ["activity", "Activity"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`subnav__link${tab === id ? " active" : ""}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </nav>
      {tab === "alerts" ? <Alerts /> : <Activity />}
    </div>
  );
}

export function NotificationsPage() {
  return (
    <Guard role="starter">
      <Inner />
    </Guard>
  );
}
